import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Switch } from "../components/ui/switch";
import { WhatsappLogo, PaperPlaneRight, CheckCircle, WarningCircle, Copy, Broadcast } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function WhatsAppSettings() {
  const { user } = useAuth();
  const [cfg, setCfg] = useState(null);
  const [form, setForm] = useState({
    access_token: "",
    phone_number_id: "",
    business_account_id: "",
    graph_api_version: "v22.0",
    business_name: "Incutech",
    enabled: false,
  });
  const [busy, setBusy] = useState(false);
  const [testTo, setTestTo] = useState("");
  const [testBody, setTestBody] = useState("Hello from Doctrack — this is a test.");
  const [testResult, setTestResult] = useState(null);

  const load = async () => {
    const r = await api.get("/settings/whatsapp");
    setCfg(r.data);
    setForm({
      access_token: "",
      phone_number_id: r.data.phone_number_id || "",
      business_account_id: r.data.business_account_id || "",
      graph_api_version: r.data.graph_api_version || "v22.0",
      business_name: r.data.business_name || "Incutech",
      enabled: !!r.data.enabled,
    });
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    setBusy(true);
    try {
      const payload = { ...form };
      if (!payload.access_token) delete payload.access_token; // preserve stored token
      const r = await api.put("/settings/whatsapp", payload);
      setCfg(r.data);
      setForm(f => ({ ...f, access_token: "" }));
      toast.success("Settings saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setBusy(false); }
  };

  const test = async () => {
    if (!testTo) { toast.error("Enter a WhatsApp number to test"); return; }
    setBusy(true); setTestResult(null);
    try {
      const r = await api.post("/settings/whatsapp/test", { to: testTo, body: testBody });
      setTestResult(r.data);
      if (r.data.mode === "meta" && r.data.status === "accepted") toast.success(`Meta accepted (wamid ${r.data.wamid || "n/a"})`);
      else if (r.data.mode === "simulated") toast.message("Simulated send (live sending is off)");
      else toast.error(`Meta error: ${r.data.error}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Test failed");
    } finally { setBusy(false); }
  };

  if (!cfg) return <div className="text-slate-500">Loading…</div>;
  const isAdmin = user?.role === "admin";
  const modePill = cfg.ready
    ? { label: "LIVE via Meta Cloud API", cls: "bg-emerald-50 border-emerald-200 text-emerald-700", icon: CheckCircle }
    : { label: "Simulated (log only)", cls: "bg-amber-50 border-amber-200 text-amber-700", icon: WarningCircle };
  const ModeIcon = modePill.icon;

  return (
    <div className="space-y-8" data-testid="whatsapp-settings-page">
      <div>
        <div className="text-xs font-mono uppercase tracking-[0.2em] text-slate-500">Integrations</div>
        <h1 className="mt-1 font-[Manrope] font-extrabold text-4xl tracking-tight flex items-center gap-3">
          <WhatsappLogo size={36} weight="duotone" className="text-emerald-600" /> WhatsApp
        </h1>
        <p className="mt-2 text-slate-600 max-w-2xl">
          Connect your Meta WhatsApp Cloud API sender to route replies and flash reports through your business line — no third-party middleware.
        </p>
      </div>

      <Card className="border border-slate-200 shadow-none rounded-md p-5 flex items-center justify-between">
        <div>
          <div className="text-[10px] uppercase tracking-widest font-mono text-slate-500 mb-1">Current mode</div>
          <span data-testid="mode-pill" className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md border text-sm font-medium ${modePill.cls}`}>
            <ModeIcon size={14} weight="fill" /> {modePill.label}
          </span>
        </div>
        <div className="text-right text-xs font-mono text-slate-500">
          <div>Business: <span className="text-slate-800">{cfg.business_name}</span></div>
          <div>Provider: <span className="text-slate-800">Meta Cloud API</span></div>
          {cfg.phone_number_id && <div>Phone ID: <span className="text-slate-800">{cfg.phone_number_id}</span></div>}
        </div>
      </Card>

      <Card className="border border-slate-200 shadow-none rounded-md p-6 space-y-4" data-testid="webhook-card">
        <div className="flex items-start justify-between gap-4">
          <div>
            <div className="flex items-center gap-2">
              <Broadcast size={20} weight="duotone" className="text-slate-700" />
              <h3 className="font-[Manrope] font-bold text-lg tracking-tight">Inbound webhook</h3>
            </div>
            <p className="text-sm text-slate-600 mt-1 max-w-2xl">
              Paste these two values into <span className="font-mono">Meta App Dashboard → WhatsApp → Configuration → Webhook</span>,
              then subscribe to <span className="font-mono">messages</span> so inbound replies land in the shared Inbox and delivery
              statuses update automatically.
            </p>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-[1fr_auto] gap-2 items-end">
          <div>
            <label className="text-[10px] uppercase tracking-widest font-mono text-slate-500">Callback URL</label>
            <Input data-testid="webhook-url" readOnly value={cfg.webhook_url || ""} className="mt-1.5 font-mono text-xs" />
          </div>
          <Button
            data-testid="copy-webhook-url"
            variant="outline"
            onClick={() => { navigator.clipboard.writeText(cfg.webhook_url || ""); toast.success("Webhook URL copied"); }}
          >
            <Copy size={14} weight="bold" className="mr-1.5" /> Copy URL
          </Button>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-[1fr_auto] gap-2 items-end">
          <div>
            <label className="text-[10px] uppercase tracking-widest font-mono text-slate-500">Verify token</label>
            <Input data-testid="webhook-verify-token" readOnly value={cfg.webhook_verify_token || ""} className="mt-1.5 font-mono text-xs" />
          </div>
          <Button
            data-testid="copy-webhook-token"
            variant="outline"
            onClick={() => { navigator.clipboard.writeText(cfg.webhook_verify_token || ""); toast.success("Verify token copied"); }}
          >
            <Copy size={14} weight="bold" className="mr-1.5" /> Copy token
          </Button>
        </div>
        <div className="text-[11px] text-slate-500 font-mono leading-relaxed">
          Meta will hit this URL with a <span className="text-slate-800">GET ?hub.mode=subscribe&amp;hub.verify_token=…</span> — we
          respond with the challenge if the token matches. Real messages then arrive via <span className="text-slate-800">POST</span>.
        </div>
      </Card>

      <Card className="border border-slate-200 shadow-none rounded-md p-6 space-y-5" data-testid="whatsapp-form">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          <div>
            <label className="text-[10px] uppercase tracking-widest font-mono text-slate-500">Business name</label>
            <Input data-testid="wa-business" className="mt-1.5" value={form.business_name} disabled={!isAdmin}
              onChange={e => setForm({ ...form, business_name: e.target.value })} />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-widest font-mono text-slate-500">Graph API version</label>
            <Input data-testid="wa-version" className="mt-1.5 font-mono" placeholder="v22.0" value={form.graph_api_version} disabled={!isAdmin}
              onChange={e => setForm({ ...form, graph_api_version: e.target.value })} />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-widest font-mono text-slate-500">Phone Number ID</label>
            <Input data-testid="wa-phone-id" className="mt-1.5 font-mono" placeholder="123456789012345"
              value={form.phone_number_id} disabled={!isAdmin}
              onChange={e => setForm({ ...form, phone_number_id: e.target.value })} />
            <div className="mt-1 text-[11px] text-slate-500">From Meta App → WhatsApp → API Setup (NOT the phone number itself).</div>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-widest font-mono text-slate-500">WhatsApp Business Account ID</label>
            <Input data-testid="wa-waba-id" className="mt-1.5 font-mono" placeholder="987654321098765"
              value={form.business_account_id} disabled={!isAdmin}
              onChange={e => setForm({ ...form, business_account_id: e.target.value })} />
          </div>
          <div className="md:col-span-2">
            <label className="text-[10px] uppercase tracking-widest font-mono text-slate-500">Access Token</label>
            <Input data-testid="wa-token" className="mt-1.5 font-mono" type="password"
              placeholder={cfg.access_token_masked ? `${cfg.access_token_masked} (leave blank to keep)` : "Paste your permanent System User access token"}
              value={form.access_token} disabled={!isAdmin}
              onChange={e => setForm({ ...form, access_token: e.target.value })} />
            <div className="mt-1 text-[11px] text-slate-500 font-mono">Never sent back to the browser after save.</div>
          </div>
        </div>

        <div className="flex items-center justify-between border-t border-slate-100 pt-4">
          <div className="flex items-center gap-3">
            <Switch data-testid="wa-enabled" checked={form.enabled} disabled={!isAdmin}
              onCheckedChange={v => setForm({ ...form, enabled: v })} />
            <div>
              <div className="text-sm font-semibold">Enable live sending</div>
              <div className="text-xs text-slate-500">When off, outbound messages are logged only (simulated).</div>
            </div>
          </div>
          <Button data-testid="wa-save" onClick={save} disabled={!isAdmin || busy} className="bg-[#0F172A] hover:bg-[#0F172A]/90 text-white">
            {busy ? "Saving…" : "Save settings"}
          </Button>
        </div>
        {!isAdmin && <div className="text-xs text-amber-700">Only admins can change these settings.</div>}
      </Card>

      <Card className="border border-slate-200 shadow-none rounded-md p-6 space-y-4" data-testid="whatsapp-test">
        <div>
          <h3 className="font-[Manrope] font-bold text-lg tracking-tight">Send a test message</h3>
          <p className="text-sm text-slate-600 mt-1">
            {cfg.ready
              ? "Calls Meta Graph API directly. Free-form text is allowed only within the 24h customer service window."
              : "Live sending is off — this will fall back to the simulated log for now."}
          </p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <Input data-testid="wa-test-to" placeholder="+15551234567" value={testTo} onChange={e => setTestTo(e.target.value)} className="font-mono" />
          <Input data-testid="wa-test-body" placeholder="Message body" value={testBody} onChange={e => setTestBody(e.target.value)} className="md:col-span-2" />
        </div>
        <div className="flex items-center justify-between">
          <div className="text-xs font-mono text-slate-500">
            {testResult && (
              <span data-testid="wa-test-result">
                mode={testResult.mode} · status={testResult.status}
                {testResult.wamid && ` · wamid=${testResult.wamid}`}
                {testResult.error && <span className="text-red-600"> · {testResult.error}</span>}
              </span>
            )}
          </div>
          <Button data-testid="wa-test-btn" onClick={test} disabled={busy || !isAdmin} className="bg-emerald-600 hover:bg-emerald-700 text-white">
            <PaperPlaneRight size={14} weight="fill" className="mr-1.5" /> Send test
          </Button>
        </div>
      </Card>

      <div className="text-xs text-slate-500 leading-relaxed max-w-2xl">
        <span className="font-semibold text-slate-700">Get the credentials:</span> in the Meta App Dashboard, go to <span className="font-mono">WhatsApp → API Setup</span> to find your Phone Number ID and WABA ID, and generate a permanent System User access token under <span className="font-mono">Business Settings → Users → System Users</span> with <span className="font-mono">whatsapp_business_messaging</span> permission.
      </div>
    </div>
  );
}
